import { useRef, type ReactNode } from 'react'
import { Float } from '@headlessui-float/react'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import { useHeadlessUiAnchor } from 'shared/feature-flags/use-headless-ui-anchor'
import CheckIcon from 'shared/icons/check-icon'
import ChevronDownIcon from 'shared/icons/chevron-down-icon'
import { TemplatesCategoryInterface } from '../types/templates-category-interface'
import { TemplatesFilterInterface } from '../types/templates-filter-interface'

const TemplatesCategorySelector = ({
  categories,
  filter,
  onChangeCategory,
}: {
  categories: TemplatesCategoryInterface[]
  filter: TemplatesFilterInterface
  onChangeCategory: (category: number | null) => void
}): ReactNode => {
  const { t } = useTranslation()
  const useNativeAnchor = useHeadlessUiAnchor()

  const category = categories.find(category => category.id === filter.category)

  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLDivElement>(null)

  const allCategories: Array<{ id: number | null; name: string }> = [
    { id: null, name: t('public.templates.categories.all_categories') },
    ...categories,
  ].filter(category => category.id !== filter.category)

  return (
    <div className="flex lg:hidden">
      <Listbox value={filter.category} onChange={onChangeCategory}>
        {({ open }) => {
          const trigger = (
            <ListboxButton className="relative w-full cursor-default rounded-lg bg-brand-primary text-left text-background-secondary focus:outline-hidden focus-visible:ring-2 focus-visible:ring-background-secondary/75 focus-visible:ring-offset-2 sm:text-sm">
              <div className="w-full py-2.5 pr-16 pl-6 text-left" ref={buttonRef}>
                <span className="block truncate">
                  {category?.id ? category?.name : t('public.templates.categories.all_categories')}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-6">
                  <ChevronDownIcon
                    className={twMerge(
                      'size-5',
                      open && 'rotate-180',
                      'right-2.5 transition-transform duration-300',
                    )}
                    aria-hidden="true"
                  />
                </span>
              </div>
            </ListboxButton>
          )

          const options = (
            <ListboxOptions
              {...(useNativeAnchor
                ? { portal: true, anchor: { to: 'bottom start' as const, gap: 4 } }
                : {})}
              className={twMerge(
                'max-h-60 w-full overflow-auto rounded-md bg-background-secondary py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm',
                useNativeAnchor && 'z-50 w-(--button-width)',
              )}
            >
              <div ref={dropdownRef} className="rounded-lg bg-background-secondary">
                {allCategories.map((category, idx) => (
                  <ListboxOption
                    key={idx}
                    className={({ focus }) =>
                      twMerge(
                        'relative cursor-default py-2.5 pl-6 select-none',
                        focus ? 'text-brand-primary' : 'text-text-primary',
                      )
                    }
                    value={category.id}
                  >
                    {({ selected }) => (
                      <>
                        <span
                          className={twMerge(
                            'block truncate font-ubuntu',
                            selected ? 'font-medium' : 'font-normal',
                          )}
                        >
                          {category.name}
                        </span>
                        {selected && (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                            <CheckIcon className="size-5" aria-hidden="true" />
                          </span>
                        )}
                      </>
                    )}
                  </ListboxOption>
                ))}
              </div>
            </ListboxOptions>
          )

          return (
            <div className="relative w-full">
              {useNativeAnchor ? (
                <>
                  {trigger}
                  {options}
                </>
              ) : (
                <Float
                  enter="transition easy-in duration-200"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition ease-out duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  onUpdate={() => {
                    if (dropdownRef.current && buttonRef.current) {
                      const buttonWidth = buttonRef.current.getBoundingClientRect().width
                      const dropdownWidth = dropdownRef.current.getBoundingClientRect().width
                      if (dropdownWidth < buttonWidth) {
                        dropdownRef.current.style.width = `${buttonWidth}px`
                      }
                    }
                  }}
                  offset={4}
                  portal
                  flip
                  as={'div'}
                >
                  {trigger}
                  {options}
                </Float>
              )}
            </div>
          )
        }}
      </Listbox>
    </div>
  )
}

export default TemplatesCategorySelector
